import { isSupportedLanguage, type SupportedLanguage } from './locale.js';

export type BrowserStorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
export type LocaleMode = 'auto' | 'manual';

export type LocalePreferenceKeys = {
  modeKey: string;
  manualLanguageKey: string;
};

export type StorageMigration = {
  version: number;
  migrate: (storage: BrowserStorageLike) => void;
};

export type RunStorageMigrationsOptions = {
  schemaVersionKey: string;
  targetVersion: number;
  migrations: readonly StorageMigration[];
};

export function getBrowserLocalStorage(): BrowserStorageLike | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

function readSchemaVersion(storage: BrowserStorageLike, key: string): number {
  const raw = storage.getItem(key);
  const parsed = Number.parseInt(raw || '', 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

export function runStorageMigrations(storage: BrowserStorageLike | null, options: RunStorageMigrationsOptions): void {
  if (!storage) return;

  const currentVersion = readSchemaVersion(storage, options.schemaVersionKey);
  const nextMigrations = [...options.migrations]
    .filter((migration) => migration.version > currentVersion && migration.version <= options.targetVersion)
    .sort((left, right) => left.version - right.version);

  for (const migration of nextMigrations) {
    migration.migrate(storage);
  }

  storage.setItem(options.schemaVersionKey, String(options.targetVersion));
}

export function removeStorageKeys(storage: BrowserStorageLike | null, keys: readonly string[]): void {
  if (!storage) return;
  for (const key of keys) {
    storage.removeItem(key);
  }
}

export function readLocalePreference(
  storage: BrowserStorageLike | null,
  keys: LocalePreferenceKeys
): { mode: LocaleMode; manualLanguage: SupportedLanguage | null } {
  if (!storage) {
    return { mode: 'auto', manualLanguage: null };
  }

  const mode = storage.getItem(keys.modeKey) === 'manual' ? 'manual' : 'auto';
  const storedLanguage = storage.getItem(keys.manualLanguageKey);
  const manualLanguage = isSupportedLanguage(storedLanguage) ? storedLanguage : null;

  if (mode === 'manual' && manualLanguage) {
    return { mode, manualLanguage };
  }

  return { mode: 'auto', manualLanguage: null };
}

export function ensureLocaleMode(
  storage: BrowserStorageLike | null,
  keys: LocalePreferenceKeys,
  mode: LocaleMode = 'auto'
): void {
  if (!storage) return;
  const existing = storage.getItem(keys.modeKey);
  if (existing === 'auto' || existing === 'manual') return;
  storage.setItem(keys.modeKey, mode);
}

export function setManualLocalePreference(
  storage: BrowserStorageLike | null,
  keys: LocalePreferenceKeys,
  language: SupportedLanguage
): void {
  if (!storage) return;
  storage.setItem(keys.modeKey, 'manual');
  storage.setItem(keys.manualLanguageKey, language);
}

export function setAutoLocalePreference(storage: BrowserStorageLike | null, keys: LocalePreferenceKeys): void {
  if (!storage) return;
  storage.setItem(keys.modeKey, 'auto');
  storage.removeItem(keys.manualLanguageKey);
}
